import styled, { css } from "styled-components";
import { breakpoints, maxBreakpoints } from "../../../../../shared-logic/themes/lexusTheme";
import type { CardVariantsType } from "./Card";
import Map from "../../../../../used-stock-cars-form/components/lexus/sidebar/CoordinatesMap.styles";

// STYLES
const service = css`
    --body-grow: 0;
    --body-width: auto;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    row-gap: var(--spacing-2);
    padding: calc(var(--spacing-3) / 2) var(--spacing-2);
    background-color: var(--color-white);
    border: 1px solid var(--color-platinum-medium);
    border-radius: 4px;

    & > div {
        display: flex;
        flex-grow: 1;
        justify-content: space-between;
        margin-inline-start: var(--spacing-1);
    }

    @media screen and (min-width: ${breakpoints.lg}) {
        flex-direction: row-reverse;
        align-items: flex-start;
        justify-content: space-between;

        & > div {
            display: block;
            margin-inline-start: 0;
        }
    }

    @media screen and (min-width: ${breakpoints.xl}) {
        --body-grow: 1;
        --body-width: 100%;
        flex-direction: column;
        justify-content: flex-start;

        & > div {
            width: 100%;
        }
    }

    @supports not (padding-inline: 0) {
        & > *:not(:nth-last-child(1)) {
            margin-bottom: var(--spacing-2);
        }
    }
`;

const accessories = css`
    --body-spacing-inline: var(--spacing-2);
    --body-spacing-block: var(--spacing-2);
    --body-width: auto;
    --footer-spacing-inline: var(--spacing-2);
    --footer-spacing-block: calc(var(--spacing-3) / 2);
    --footer-border-top-color: var(--color-platinum-light);
    display: flex;
    flex-shrink: 0;
    flex-wrap: wrap;
    align-items: flex-start;
    background-color: var(--color-white);
    border: 1px solid var(--color-platinum-medium);
    border-radius: 4px;

    img {
        width: 100%;
        height: auto;
        aspect-ratio: 1 / 1;
        object-fit: cover;
        background-color: var(--color-mist);
        border-top-left-radius: 4px;
    }

    @media screen and (min-width: ${breakpoints.xl}) {
        --body-width: 100%;
        flex-direction: column;
        align-items: flex-start;
        align-self: stretch;

        img {
            height: auto;
            border-top-right-radius: 4px;
        }
    }
`;

const accessoriesInPack = css`
    --body-spacing-inline: 0;
    --body-spacing-block: var(--spacing-2) 0;
    --body-width: auto;
    --footer-spacing-inline: var(--spacing-2);
    --footer-spacing-block: calc(var(--spacing-3) / 2);
    --footer-border-top-color: var(--color-platinum-light);
    display: flex;
    flex-shrink: 0;
    flex-wrap: wrap;
    align-items: flex-start;
    background-color: var(--color-white);

    img {
        width: 100%;
        height: auto;
        aspect-ratio: 1 / 1;
        object-fit: cover;
        background-color: var(--color-cloud);
    }

    @media screen and (min-width: ${breakpoints.xl}) {
        --body-width: 100%;
        flex-direction: column;
        align-items: flex-start;
        align-self: stretch;

        img {
            height: auto;
        }
    }
`;

const pack = css`
    --header-spacing-inline: var(--spacing-2);
    --header-spacing-block: var(--spacing-2);
    --body-spacing-inline: var(--spacing-2);
    --body-spacing-block: var(--spacing-3);
    --footer-spacing-inline: var(--spacing-2);
    --footer-spacing-block: calc(var(--spacing-3) / 2);
    --footer-border-top-color: var(--color-platinum-light);
    background-color: var(--color-white);
    border-radius: 4px;
    box-shadow: 0 0 0 1px var(--color-platinum-medium);

    &[aria-selected="true"] {
        box-shadow: 0 0 0 2px var(--color-titanium);
    }
`;

const contactdealder = css`
    --body-spacing-inline: var(--spacing-3);
    --body-spacing-block: var(--spacing-3);
    --body-width: auto;
    display: flex;
    flex-shrink: 0;
    flex-wrap: wrap;
    align-items: flex-start;
    background-color: var(--color-white);
    border: 1px solid var(--color-platinum-light);
    border-radius: 8px;

    img {
        width: 100%;
        height: auto;
        aspect-ratio: 16/9;
        object-fit: cover;
        background-color: var(--color-cloud);
        border-top-left-radius: 8px;

        &[data-aspect-ratio="4:3"] {
            aspect-ratio: 4/3;
        }
    }

    @media screen and (min-width: ${breakpoints.xl}) {
        --body-width: 100%;
        flex-direction: column;
        align-items: flex-start;
        align-self: stretch;

        img {
            height: auto;
            border-top-right-radius: 8px;
        }
    }
`;

const outline = css`
    display: flex;
    align-items: flex-start;
    align-self: stretch;
    padding: var(--spacing-3);
    border: 1px solid var(--color-platinum-light);
`;

// COMPONENTS
export const Header = styled.header`
    --_header-spacing-inline: var(--header-spacing-inline, 0);
    --_header-spacing-block: var(--header-spacing-block, 0);
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-inline: var(--_header-spacing-inline);
    padding-block: var(--_header-spacing-block);

    & > div {
        display: flex;
        align-items: center;
        column-gap: var(--spacing-1);
    }

    @supports not (padding-inline: 0) {
        padding-top: var(--_header-spacing-block);
        padding-bottom: var(--_header-spacing-block);
        padding-left: var(--_header-spacing-inline);
        padding-right: var(--_header-spacing-inline);

        svg,
        .dxp-icon {
            margin-right: var(--spacing-1);
        }
    }
`;

export const Body = styled.div`
    --_body-spacing-inline: var(--body-spacing-inline, 0);
    --_body-spacing-block: var(--body-spacing-block, 0);
    --_body-width: var(--body-width, 100%);
    --_body-height: var(--body-height, 100%);
    --_body-grow: var(--body-grow, 1);
    flex-grow: var(--_body-grow);
    align-self: stretch;
    width: var(--_body-width);

    @media screen and (max-width: ${maxBreakpoints.md}) {
        height: var(--_body-height);
    }

    &:not(:empty) {
        padding-inline: var(--_body-spacing-inline);
        padding-block: var(--_body-spacing-block);

        ${Header} + & {
            padding-block-start: 0;
        }

        @supports not (padding-inline: 0) {
            padding-top: var(--_body-spacing-block);
            padding-bottom: var(--_body-spacing-block);
            padding-left: var(--_body-spacing-inline);
            padding-right: var(--_body-spacing-inline);
        }
    }
`;

export const Footer = styled.div`
    --_footer-spacing-inline: var(--footer-spacing-inline, 0);
    --_footer-spacing-block: var(--footer-spacing-block, 0);
    --_footer-border-top-color: var(--footer-border-top-color, transparent);
    display: flex;
    justify-content: space-between;
    width: 100%;

    &:not(:empty) {
        padding-inline: var(--_footer-spacing-inline);
        padding-block: var(--_footer-spacing-block);
        border-top: 1px solid var(--_footer-border-top-color);

        @supports not (padding-inline: 0) {
            padding-top: var(--_footer-spacing-block);
            padding-bottom: var(--_footer-spacing-block);
            padding-left: var(--_footer-spacing-inline);
            padding-right: var(--_footer-spacing-inline);
        }
    }

    & > div:nth-child(2) {
        display: flex;
        align-items: flex-start;
        column-gap: var(--spacing-2);

        & > span {
            line-height: 3.2rem; /* Text should be the same height as checkbox */
        }

        & > &:not(:nth-last-child(1)) {
            margin-right: var(--spacing-2);
        }
    }
`;

const delivery = css`
    --header-spacing-inline: var(--spacing-2);
    --header-spacing-block: var(--spacing-2);
    --body-spacing-inline: var(--spacing-2);
    --body-spacing-block: var(--spacing-3);
    --footer-spacing-inline: 0;
    --footer-spacing-block: calc(var(--spacing-3) / 2);
    --footer-border-top-color: var(--color-platinum-light);
    background-color: var(--color-white);
    border-radius: 4px;
    box-shadow: 0 0 0 1px var(--color-platinum-medium);

    &[aria-selected="true"] {
        box-shadow: 0 0 0 2px var(--color-titanium);
    }
`;

export const MapWrapper = styled.div`
    width: 100%;
    min-height: 188px;
    aspect-ratio: 16 / 9;
    border-top-left-radius: 8px;
    overflow: hidden;

    &[data-aspect-ratio="4:3"] {
        @media screen and (min-width: ${breakpoints.md}) and (max-width: ${maxBreakpoints.md}) {
            height: auto;
            aspect-ratio: 4 / 3;

            ${Map} {
                height: 100%;
            }
        }
    }

    @media screen and (min-width: ${breakpoints.xl}) {
        border-top-right-radius: 8px;
    }
`;

export const CardVariants = {
    service,
    accessories,
    "accessories-in-pack": accessoriesInPack,
    contactdealer: contactdealder,
    pack,
    outline,
    delivery,
};

const CardVariant = styled.div<{ variant: CardVariantsType }>`
    ${({ variant }) =>
        variant &&
        css`
            ${CardVariants[variant]};
        `};
`;

export default CardVariant;
